import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider, ProtectedRoute, useAuth } from './components/AuthGuard';
import { DocklyLogo } from '@/components/brand/DocklyLogo';
import { ThemeToggle } from '@/components/brand/ThemeToggle';
import { LanguageToggle } from '@/components/brand/LanguageToggle';
import { AppSidebar } from '@/components/shell/AppSidebar';
import { MobileTabBar } from '@/components/shell/MobileTabBar';
import { CommandPalette } from '@/components/shell/CommandPalette';
import { mindestrolleFuerPfad } from '@/lib/navigation';

// Lazy load pages for code splitting
const Landing = lazy(() => import('./pages/Landing'));
const AnalysisStatus = lazy(() => import('./pages/AnalysisStatus'));
const Bericht = lazy(() => import('./pages/Bericht'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminAnalyses = lazy(() => import('./pages/admin/AdminAnalyses'));
const AdminVergleich = lazy(() => import('./pages/admin/AdminVergleich'));
const AdminBranchen = lazy(() => import('./pages/admin/AdminBranchen'));
const AdminTextbausteine = lazy(() => import('./pages/admin/AdminTextbausteine'));
const AdminFlags = lazy(() => import('./pages/admin/AdminFlags'));
const AdminPakete = lazy(() => import('./pages/admin/AdminPakete'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminCustomerDetail = lazy(() => import('./pages/admin/AdminCustomerDetail'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminQueryProfiles = lazy(() => import('./pages/admin/AdminQueryProfiles'));
const AdminQueryProfileEditor = lazy(() => import('./pages/admin/AdminQueryProfileEditor'));
const AdminApiHistory = lazy(() => import('./pages/admin/AdminApiHistory'));
const AdminDesigns = lazy(() => import('./pages/admin/AdminDesigns'));
const AdminDesignEditor = lazy(() => import('./pages/admin/AdminDesignEditor'));
const AdminUsageDashboard = lazy(() => import('./pages/admin/AdminUsageDashboard'));
const EmbedPage = lazy(() => import('./pages/EmbedPage'));

// Loading fallback component
function LoadingFallback() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen app-background flex items-center justify-center">
      <div className="glass-surface p-8 rounded-2xl">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-surface-600 font-medium">{t('common.loading')}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Rahmen des Verwaltungsbereichs.
 *
 * Aufbau nach `apple-struktur.md`: Sidebar ab Tablet-Breite, darunter die
 * schwebende Tab-Leiste. Beide lesen aus derselben Navigations-Registry, die
 * Palette ebenfalls — es gibt kein zweites Menü, das nachgepflegt werden muss.
 *
 * Dieselbe Registry sagt, welche Rolle eine Seite mindestens verlangt
 * (`mindestrolle`, z. B. die Design-Verwaltung: GLOBAL_ADMIN). Menü und Zugang
 * können so nicht auseinanderlaufen. Beides ist kosmetisch — das Backend
 * entscheidet, ob ein Aufruf gelingt.
 */
function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <ProtectedRoute requiredRole={mindestrolleFuerPfad(pathname)}>
      <div className="flex min-h-screen bg-surface-50">
        <AppSidebar className="hidden md:flex" rolle={user?.role} />

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b border-surface-200 bg-surface-100/85 px-4 py-3 backdrop-blur-xl sm:px-6">
            {/* Mobil traegt die Kopfzeile die Marke — dort gibt es keine Sidebar. */}
            <Link to="/admin" aria-label={t('nav.home')} className="md:hidden">
              <DocklyLogo height={24} />
            </Link>

            <div className="ml-auto flex items-center gap-3">
              <LanguageToggle />
              <ThemeToggle />

              <div className="flex items-center gap-2 border-l border-surface-200 pl-3">
                <span className="hidden text-xs text-surface-500 sm:inline">{user?.email}</span>
                <span className="rounded bg-surface-200 px-1.5 py-0.5 text-[10px] font-medium text-surface-700">
                  {user?.role}
                </span>
                <button
                  onClick={logout}
                  className="text-sm text-surface-500 transition-colors hover:text-[color:var(--color-error)]"
                >
                  {t('auth.logout')}
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 p-4 pb-28 sm:p-6 lg:p-8 md:pb-8">
            <div className="mx-auto max-w-7xl">{children}</div>
          </main>
        </div>

        <MobileTabBar rolle={user?.role} />
        <CommandPalette rolle={user?.role} />
      </div>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/analysis/:id" element={<AnalysisStatus />} />
            <Route path="/analysis/:id/report" element={<Bericht />} />

            {/* Embed route */}
            <Route path="/embed/:snippetId" element={<EmbedPage />} />

            {/* Admin login */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Admin routes (protected) */}
            <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
            <Route path="/admin/analyses" element={<AdminLayout><AdminAnalyses /></AdminLayout>} />
            <Route path="/admin/vergleich" element={<AdminLayout><AdminVergleich /></AdminLayout>} />
            <Route path="/admin/customers" element={<AdminLayout><AdminCustomers /></AdminLayout>} />
            <Route path="/admin/customers/:id" element={<AdminLayout><AdminCustomerDetail /></AdminLayout>} />
            <Route path="/admin/branchen" element={<AdminLayout><AdminBranchen /></AdminLayout>} />
            <Route path="/admin/textbausteine" element={<AdminLayout><AdminTextbausteine /></AdminLayout>} />
            <Route path="/admin/flags" element={<AdminLayout><AdminFlags /></AdminLayout>} />
            <Route path="/admin/pakete" element={<AdminLayout><AdminPakete /></AdminLayout>} />
            <Route path="/admin/query-profiles" element={<AdminLayout><AdminQueryProfiles /></AdminLayout>} />
            <Route path="/admin/query-profiles/:id" element={<AdminLayout><AdminQueryProfileEditor /></AdminLayout>} />
            <Route path="/admin/api-history" element={<AdminLayout><AdminApiHistory /></AdminLayout>} />
            <Route path="/admin/designs" element={<AdminLayout><AdminDesigns /></AdminLayout>} />
            <Route path="/admin/designs/:id" element={<AdminLayout><AdminDesignEditor /></AdminLayout>} />
            <Route path="/admin/usage" element={<AdminLayout><AdminUsageDashboard /></AdminLayout>} />
            <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </ErrorBoundary>
  );
}
